import * as Accordion from "#root/components/accordion";
import { Case, Switch } from "#root/components/conditionals";
import * as Fields from "#root/components/form/fields";
import { GlyphLoading } from "#root/components/glyphs";
import Sidebar from "#root/components/sidebar";
import StandardEditor from "#root/components/standard-editor";
import { getUserCollectionForAccount, type UserListItem } from "#root/schema/user";
import { useData } from "#root/utility/hooks";
import { mergeUpdate } from "#root/utility/merge-update";
import type { RecordModificationResponse } from "#root/utility/remote-data";
import type { ReactNode } from "react";
import { useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import PageLayout from "./layout";

export default function AdminUsersPage({ accountId, userId: id }): ReactNode {
  const navigate = useNavigate();
  const collection = useMemo(() => getUserCollectionForAccount(accountId), [accountId]);

  const initialData = useMemo(
    () => ({
      accountId,
    }),
    [accountId],
  );

  const [users, setUsers] = useData(async () => {
    const { rows } = await collection.list();
    return rows;
  }, [accountId]);

  const handleUpdate = useCallback(
    (data: RecordModificationResponse<UserListItem>) => {
      setUsers(mergeUpdate(users!, data));

      if ("deleted" in data) {
        navigate(`/admin/account/${accountId}/user`);
      } else if ("created" in data) {
        navigate(`/admin/account/${accountId}/user/${data.created}`);
      }
    },
    [navigate, accountId, setUsers, users],
  );

  if (!users) {
    return (
      <PageLayout title="Users">
        <section>
          <GlyphLoading/>
        </section>
      </PageLayout>
    );
  }

  return (
    <PageLayout title="Users">
      <UserSidebar {...{ users, id, accountId }} />
      <section>
        <Switch>
          <Case expr={id === "new"}>
            <h1>New User</h1>
            <Editor collection={collection} initialData={initialData} onSave={handleUpdate}/>
          </Case>
          <Case expr={id != null}>
            <h1>Edit User</h1>
            <Editor id={id} collection={collection} onSave={handleUpdate} onDelete={handleUpdate}/>
          </Case>
        </Switch>
      </section>
    </PageLayout>
  );
}

function UserSidebar({ users, id, accountId }) {
  const navigate = useNavigate();
  return (
    <Sidebar>
      <button className="flat center" type="button" onClick={() => navigate(`/admin/account/${accountId}/user/new`)}>
        Add User
      </button>
      <Accordion.Parent initialItemId={id}>
        <Accordion.Group label="By Name">
          {users.map(({ id, name, email }) => (
            <Accordion.Item key={id} itemId={id}>
              <Link to={`/admin/account/${accountId}/user/${id}`}>{name || email || id}</Link>
            </Accordion.Item>
          ))}
        </Accordion.Group>
      </Accordion.Parent>
    </Sidebar>
  );
}

function Editor(props) {
  return (
    <StandardEditor {...props}>
      <Fields.Set name="accountId">
        <Fields.Hidden/>
      </Fields.Set>
      <Fields.Set name="id">
        <Fields.Hidden/>
      </Fields.Set>
      <Fields.Set name="name">
        <label>Your Name</label>
        <Fields.FullName required/>
        <Fields.Validity/>
      </Fields.Set>
      <Fields.Set name="phone">
        <label>Phone Number</label>
        <Fields.Phone/>
        <Fields.Validity/>
      </Fields.Set>
      <Fields.Set name="email">
        <label>Email Address</label>
        <Fields.Email required/>
        <Fields.Validity/>
      </Fields.Set>
      <Fields.Set name="password">
        <label>New Password</label>
        <Fields.Password autoComplete="new-password"/>
        <Fields.Validity/>
      </Fields.Set>
      <Fields.Set name="isSuperAdmin">
        <label>User Permissions</label>
        <Fields.Select>
          <Fields.Option value={0}>Normal User</Fields.Option>
          <Fields.Option value={1}>Super Administrator</Fields.Option>
        </Fields.Select>
        <Fields.Validity/>
      </Fields.Set>
    </StandardEditor>
  );
}
